import { useMemo, useRef } from 'react';
import _ from 'lodash';
import useAPI from 'services/useAPI';
import { useQuery, useMutation } from '@tanstack/react-query';
import createQueryRequest from 'services/createQueryRequest';
import createMutationRequest from 'utils/createMutationRequest';

export const modelKeys = {
  all: ['models'],
  models: () => [...modelKeys.all],
  /**
   * @param {string} id
   * @returns {array}
   */
  model: (id) => [...modelKeys.models(), id],
  modelDrafts: () => ['modelDrafts'],
  modelDraft: (id) => [...modelKeys.modelDrafts(), id],
  bindings: () => ['bindings'],
  binding: (id) => [...modelKeys.bindings(), id],
  bindingByModelId: (modelId) => [...modelKeys.bindings(), modelId],
  bindingDrafts: () => ['bindingDrafts']
};


export const useModelsQuery = ({ includeDrafts }) => {
  const api = useAPI();
  const modelsQuery = useQuery({
    queryKey: modelKeys.models(),
    queryFn: createQueryRequest(api.core.getModels),
    initialData: []
  });

  const draftsQuery = useQuery({
    enabled: includeDrafts,
    queryKey: modelKeys.modelDrafts(),
    queryFn: createQueryRequest(api.core.getModelDrafts),
    initialData: []
  });

  const models = useMemo(() => {
    return [
      ...modelsQuery.data,
      ...draftsQuery.data
    ];
  }, [draftsQuery.data, modelsQuery.data]);

  return {
    models,
    modelsQuery,
    draftsQuery,
    isLoading: (includeDrafts) ? modelsQuery.isLoading || draftsQuery.isLoading : modelsQuery.isLoading
  };
};

export const useModelQuery = (id, modelDraft = false) => {
  const api = useAPI();

  const modelQuery = useQuery({
    enabled: !!id && !modelDraft,
    queryKey: modelKeys.model(id),
    queryFn: createQueryRequest(api.core.getModel, id),
    staleTime: 3600000
  });

  const draftQuery = useQuery({
    enabled: !!id && modelDraft,
    queryKey: modelKeys.model(id),
    queryFn: createQueryRequest(api.core.getModelDraft, id)
  });

  return {
    model: modelQuery.data ?? draftQuery.data,
    query: modelQuery ?? draftQuery,
    isLoading: modelQuery.isInitialLoading ?? draftQuery.isInitialLoading
  };
};
export const useBindingsQuery = ({ includeDrafts }) => {
  const api = useAPI();
  const bindingsQuery = useQuery({
    queryKey: modelKeys.bindings(),
    queryFn: createQueryRequest(api.core.getBindings),
    initialData: []
  });

  const draftsQuery = useQuery({
    enabled: includeDrafts,
    queryKey: modelKeys.bindingDrafts(),
    queryFn: createQueryRequest(api.core.getBindingDrafts),
    initialData: []
  });

  const bindings = useMemo(() => {
    return [
      ...bindingsQuery.data,
      ...draftsQuery.data
    ];
  }, [draftsQuery.data, bindingsQuery.data]);

  return {
    bindings,
    modelsQuery: bindingsQuery,
    draftsQuery,
    isLoading: (includeDrafts) ? bindingsQuery.isLoading || draftsQuery.isLoading : bindingsQuery.isLoading
  };
};
/**
 * @typedef {object} BindingType
 * @property {string} id
 * @property {string} displayName
 * @property {string} assetModelId
 * @property {boolean} draft
 * @property {string} sources
 * @property {string} target
 * @property {string} targetProperty
 */

/**
 * @typedef {object} BindingData
 * @property {BindingType[]} bindings
 * @property {import('@tanstack/react-query').UseQueryResult} bindingsQuery
 * @property {import('@tanstack/react-query').UseQueryResult} bindingDraftsQuery
 * @property {boolean} isLoading
 */

/**
 *
 * @param {string} modelId
 * @param {boolean} includeDrafts
 * @returns {BindingData}
 */
export const useBindingsByModel = (modelId, includeDrafts) => {
  const api = useAPI();

  const bindingsQuery = useQuery({
    enabled: !!modelId,
    queryKey: modelKeys.bindingByModelId(modelId),
    queryFn: createQueryRequest(api.core.getBindingsByModelId),
    retry: false
  });
  const bindingDraftsQuery = useQuery({
    enabled: !!modelId && includeDrafts,
    queryKey: modelKeys.bindingDrafts(),
    queryFn: createQueryRequest(api.core.getBindingDrafts)
  });

  const bindings = useMemo(() => {
    const bindings = _.map(bindingsQuery.data ?? [], binding => {
      return {
        id: binding['@id'],
        ...binding
      };
    });
    // remove all draft bindings that doesn't match the modelId
    const draftBindings = _.filter(bindingDraftsQuery.data ?? [], { assetModelId: modelId });
    return _.concat(bindings, draftBindings);
  }, [bindingsQuery.data, bindingDraftsQuery.data, modelId]);

  return {
    bindings,
    bindingsQuery,
    bindingDraftsQuery,
    isLoading: (includeDrafts) ? bindingsQuery.isLoading || bindingDraftsQuery.isLoading : bindingsQuery.isLoading
  };
};

export const useBindingQuery = (bindingId, bindingDraft = false, defaultBinding = {}, retry = true) => {
  const api = useAPI();


  const bindingQuery = useQuery({
    enabled: !!bindingId && !bindingDraft,
    queryKey: modelKeys.binding(bindingId),
    queryFn: createQueryRequest(api.core.getBinding, bindingId),
    retry: retry
  });

  const draftQuery = useQuery({
    enabled: !!bindingId && bindingDraft,
    queryKey: modelKeys.binding(bindingId),
    queryFn: createQueryRequest(api.core.getBindingDraft, bindingId),
    initialData: defaultBinding,
    retry: retry
  });

  if (bindingId == null) {
    return {
      binding: defaultBinding,
      isLoading: false,
      query: {}
    };
  }
  return {
    binding: bindingDraft ? draftQuery.data : bindingQuery.data,
    isLoading: bindingDraft ? draftQuery.isInitialLoading : bindingQuery.isInitialLoading,
    query: bindingDraft ? draftQuery : bindingQuery
  };
};


/**
 * Generate a default binding for a model and endpoint and post it to beacon tower
 *
 * @returns {Object} generateQuery - the query to generate the default binding
 * @returns {Object} createQuery - the query to create the default binding in beacon tower
 * @returns {Function} createDefaultBinding - the function to call to generate and create the default binding
 */
export const useDefaultBinding = ({ createOnSuccess = () => {} }) => {
  const api = useAPI();
  const createData = useRef({});
  const bindingDescriptionId = useRef(null);

  const generateQuery = useMutation({
    mutationFn: createMutationRequest(api.core.generateBinding),
    onSuccess: (binding) => {
      bindingDescriptionId.current = binding['@id'];
      const bindingDescription = {
        id: binding['@id'],
        displayName: binding?.displayName,
        description: binding?.description,
        modelData: binding,
        draft: false,
        ...createData.current
      };
      createQuery.mutate(bindingDescription);
    }
  });

  const createQuery = useMutation({
    mutationFn: createMutationRequest(api.core.createBinding),
    onSuccess: createOnSuccess,
    onError: (error) => {
      console.error('error', error);
    }
  });

  const createDefaultBinding = ({ modelId, endpointId, organizationId }) => {
    createData.current = { assetModelId: modelId, sources: [endpointId], organizationId };
    generateQuery.mutate({ modelId, endpointId });
  };

  return {
    createDefaultBinding,
    generateQuery,
    createQuery,
    isCreating: generateQuery.isLoading || createQuery.isLoading,
    bindingDescriptionId: bindingDescriptionId.current
  };
};

